import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { tryGetQuoteIsin } from '@zero/shared/util-helper';
import { InstrumentType, TopInstrument } from '@zero/shared/util-models';
import { Indication } from '../entities/indication.model';
import { homeActions, homeApiActions } from './home.actions';

type LoadingState = 'INITIAL' | 'LOADING' | 'LOADED' | 'ERROR';

export interface HomeState {
  indications: Indication[];
  indicationsISINs: string[];
  indicationsQuoteISINs: string[];
  indicationsLoadingState: LoadingState;
  mostTradedInstruments: TopInstrument[];
  mostTradedInstrumentsISINs: string[];
  mostTradedInstrumentsLoadingState: LoadingState;
}

export const homeInitialState: HomeState = {
  indications: [],
  indicationsISINs: [],
  indicationsQuoteISINs: [],
  indicationsLoadingState: 'INITIAL',
  mostTradedInstruments: [],
  mostTradedInstrumentsISINs: [],
  mostTradedInstrumentsLoadingState: 'INITIAL',
};

export const homeFeature = createFeature({
  name: 'home',
  reducer: createReducer(
    homeInitialState,
    on(
      homeActions.setFamousIndications,
      (state, { indications }): HomeState => ({
        ...state,
        indications,
        indicationsISINs: indications.map(({ isin }) => isin),
        indicationsQuoteISINs: indications.map(({ isin }) => tryGetQuoteIsin(isin)),
      }),
    ),
    on(
      homeActions.updateFamousIndications,
      (state, { indications }): HomeState => ({
        ...state,
        indicationsLoadingState: 'LOADED',
        indications,
      }),
    ),
    on(homeActions.loadTopInstruments, (state): HomeState => ({ ...state, mostTradedInstrumentsLoadingState: 'LOADING' })),
    on(
      homeApiActions.loadTopInstrumentsSuccessful,
      (state, { topInstruments }): HomeState => ({
        ...state,
        mostTradedInstrumentsLoadingState: 'LOADED',
        mostTradedInstruments: topInstruments,
        mostTradedInstrumentsISINs: topInstruments.map(({ isin }) => isin),
      }),
    ),
    on(
      homeApiActions.loadTopInstrumentsFailed,
      (state): HomeState => ({
        ...state,
        mostTradedInstrumentsLoadingState: 'ERROR',
      }),
    ),
    on(
      homeActions.updateTopInstruments,
      (state, { topInstruments }): HomeState => ({
        ...state,
        mostTradedInstruments: topInstruments,
      }),
    ),
  ),
  extraSelectors: ({ selectIndicationsLoadingState, selectMostTradedInstruments, selectMostTradedInstrumentsLoadingState }) => ({
    selectIndicationsLoading: createSelector(selectIndicationsLoadingState, (state) => state === 'LOADING'),
    selectTopInstrumentsByType: (type: InstrumentType) =>
      createSelector(selectMostTradedInstruments, (instruments) => instruments.filter((instrument) => instrument.type === type)),
    selectMostTradedInstrumentsLoading: createSelector(selectMostTradedInstrumentsLoadingState, (state) => state === 'LOADING'),
  }),
});
